import { useMemo } from 'react'
import { flow } from '@ally/utilitarian'
import { withAppHeaders, withContentType, toJSON } from '@ally/fido'
import { useDataDomain, DomainFetch, LoadState } from '@ally/use-data-domain'
import logRocket from 'logrocket'

import { FocusDebtAccount } from '../types'
import { DebtPlanFocusAccountValue } from './DebtPlanFocusAccountContext'
import { getRoles } from '../utils'
import { useHostServices } from '../HostServices'

export type FetchHookState<T> = {
  isLoaded: boolean
  isLoading: boolean
  isError: boolean
  data: T | null
  update: () => void
}

function useGetDebtPlanFocusAccount(): DebtPlanFocusAccountValue {
  const { session, featureFlags, log } = useHostServices()
  const accessToken = session.data?.access_token
  const roles = getRoles(session)
  const showDebtPlan =
    featureFlags.variation('FF_debt-plan-snapshot', false) && roles.debtPlan

  const fetcher: () => Promise<DomainFetch<FocusDebtAccount>> = async () => {
    const response = await fetch(
      '/acs/debt-plan-svc/debt-accounts/focus',
      flow(
        withAppHeaders,
        withContentType,
      )({
        headers: {
          // /sts token for trasnmit v2 API's
          Authorization: `Bearer ${accessToken}`,
        },
      }),
    )

    const payload = await toJSON<FocusDebtAccount>(response)

    if (!response.ok) {
      const unexpectedError = `Debt Plan fetch - Unexpected Error`
      logRocket.captureMessage(`ally-next-remote-dashboard: ${unexpectedError}`)
      log.error({
        message: unexpectedError,
      })
      throw new Error(unexpectedError)
    }

    return {
      payload,
      response,
    }
  }

  const { data, error, status, update } = useDataDomain<FocusDebtAccount>({
    fetch: fetcher,
    fireLoad: showDebtPlan && accessToken,
  })

  return useMemo(() => {
    return {
      data,
      isError: !!error || !!(data as any)?.error,
      isLoading: status.loadState === LoadState.LOADING,
      isLoaded: [LoadState.LOADED, LoadState.FAILED].includes(status.loadState),
      update,
    }
  }, [data, error, status, update])
}

export default useGetDebtPlanFocusAccount
